import { api } from '../../../../core/utils/apiClient';
import {
  AdminSubscriptionPlan,
  CreatePlanRequest,
  UpdatePlanRequest,
} from '../types/billing.types';

export class AdminBillingService {
  /**
   * Create a new subscription plan
   */
  async createPlan(planData: CreatePlanRequest): Promise<AdminSubscriptionPlan | null> {
    try {
      const response = await api.post('/api/v1/billing/admin/plans/create', planData);
      if (response.ok) {
        return await response.json();
      }
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create plan');
    } catch (error: any) {
      console.error('Error creating plan:', error);
      throw error;
    }
  }

  /**
   * Get detailed information about a specific plan (including inactive)
   */
  async getPlanById(planId: string): Promise<AdminSubscriptionPlan | null> {
    try {
      const response = await api.get(`/api/v1/billing/admin/plans/${planId}`);
      if (response.ok) {
        return await response.json();
      }
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to fetch plan');
    } catch (error: any) {
      console.error('Error fetching plan:', error);
      throw error;
    }
  }

  /**
   * Update an existing subscription plan
   */
  async updatePlan(planId: string, planData: UpdatePlanRequest): Promise<AdminSubscriptionPlan | null> {
    try {
      const response = await api.put(`/api/v1/billing/admin/plans/update/${planId}`, planData);
      if (response.ok) {
        return await response.json();
      }
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update plan');
    } catch (error: any) {
      console.error('Error updating plan:', error);
      throw error;
    }
  }

  /**
   * Delete a subscription plan
   */
  async deletePlan(planId: string): Promise<boolean> {
    try {
      const response = await api.delete(`/api/v1/billing/admin/plans/delete/${planId}`);
      return response.ok || response.status === 204;
    } catch (error: any) {
      console.error('Error deleting plan:', error);
      throw error;
    }
  }

  /**
   * Get all plans (including inactive) - using the public endpoint for now
   * Admin can access inactive plans through the getPlanById endpoint
   */
  async getAllPlans(): Promise<AdminSubscriptionPlan[]> {
    try {
      const response = await api.get('/api/v1/billing/plans');
      if (response.ok) {
        return await response.json();
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching plans:', error);
      return [];
    }
  }
}

export const adminBillingService = new AdminBillingService();
